import {
  getCheckoutProductsInGtmFormat,
  getHashedEmail,
  formatProduct,
} from './external-services-helper'

export const ACTIONS = {
  addToCart: 'add-to-cart',
  errors: 'errors',
  checkout: 'checkout',
  filterAndSorting: 'filter-and-sorting',
  listClick: 'list-click',
  listView: 'list-view',
  navigation: 'navigation',
  option: 'option',
  productView: 'product-view',
  productInteraction: 'product-interaction',
  promotionView: 'promotion-view',
  promotionClick: 'promotion-click',
  purchase: 'purchase',
  quiz: 'quiz',
  removeFromCart: 'remove-from-cart',
  shoeFinder: 'shoe-finder',
  shoeComparison: 'shoe-comparison',
  shoeMatcher: 'shoe-matcher',
  sizeGuide: 'size-guide',
  subscriptions: 'subscriptions',
  userAccount: 'user-account',
  virtPath: 'virt-path',
}

export const CONTEXTS = {
  navigationTeaser: 'navigation teaser',
  promoCard: 'promo card',
  search: 'search results',
  recommendation: 'recommendation',
  shoeFinder: 'shoe finder',
  shoeComparison: 'shoe comparison',
}

export default {
  [ACTIONS.addToCart]: {
    gtmEcomAddToCart: ({ product }) => {
      return {
        ecommerce: {
          currencyCode: product.currency,
          add: {
            products: [
              {
                id: product.productSku,
                price: product.price,
                dimension27: product.size,
                dimension30: product.sku,
                quantity: product.quantity,
              },
            ],
          },
        },
      }
    },
  },

  [ACTIONS.checkout]: {
    gtmEcomCheckout: ({ data }) => {
      const products = getCheckoutProductsInGtmFormat(data.items)
      return {
        checkoutVersion: data.checkoutVersion,
        isLoggedIn: data.isLoggedIn,
        ecommerce: {
          currencyCode: data.currency,
          checkout: {
            actionField: { step: data.checkoutStep },
            products,
          },
        },
      }
    },
    rockerBoxCheckout: ({ data }) => {
      return {
        on: {
          order: {
            number: data.orderNumber,
            state: data.checkoutStepName,
            email: data.orderEmail,
            hashedEmail: getHashedEmail(data.orderEmail),
          },
        },
      }
    },
  },

  [ACTIONS.errors]: {
    gtmEvent: ({ details }) => {
      return {
        eventCategory: 'errors',
        eventAction: details.action,
        eventLabel: details.label,
        nonInteraction: false,
      }
    },
  },

  [ACTIONS.filterAndSorting]: {
    gtmEvent: ({ details }) => {
      return {
        eventCategory: 'filter_and_sorting',
        eventAction: details.action,
        eventLabel: details.label,
        nonInteraction: false,
      }
    },
  },

  [ACTIONS.listView]: {
    gtmEcomProductImpression: ({ productData }) => {
      return {
        ecommerce: {
          currencyCode: productData.currency,
          impressions: productData.items,
        },
      }
    },
  },

  [ACTIONS.listClick]: {
    gtmEcomProductClick: ({ productData }) => {
      return {
        ecommerce: {
          currencyCode: productData.currency,
          click: {
            actionField: { list: productData.list },
            products: [productData.items],
          },
        },
      }
    },
  },

  [ACTIONS.navigation]: {
    gtmEvent: ({ details }) => {
      return {
        eventCategory: 'navigation',
        eventAction: details.action,
        eventLabel: details.label,
        nonInteraction: Boolean(details.nonInteraction),
      }
    },
  },

  [ACTIONS.option]: {
    gtmEcomCheckoutOption: ({ brand }) => {
      return {
        ecommerce: {
          checkout_option: {
            actionField: { step: 4, option: brand },
          },
        },
      }
    },
  },

  [ACTIONS.productInteraction]: {
    gtmEvent: ({ details }) => {
      return {
        eventCategory: 'product_interactions',
        eventAction: details.action,
        eventLabel: details.label,
        nonInteraction: Boolean(details.nonInteraction),
      }
    },
  },

  [ACTIONS.productView]: {
    gtmEcomDetailView: ({ details }) => {
      const { sku, price } = details.variant.spreeProduct
      const currencyCode = details.currency
      return {
        ecommerce: {
          currencyCode,
          detail: {
            products: [{ id: sku, price }],
          },
        },
      }
    },
  },

  [ACTIONS.promotionView]: {
    gtmEcomPromotionView: ({ promotions }) => {
      return {
        ecommerce: {
          promoView: {
            promotions,
          },
        },
      }
    },
  },

  [ACTIONS.promotionClick]: {
    gtmEcomPromotionClick: ({ promotions }) => {
      return {
        ecommerce: {
          promoClick: {
            promotions,
          },
        },
      }
    },
  },

  [ACTIONS.purchase]: {
    avantLinkPurchase: ({ data }) => {
      const productsForAvant = data.items.map(lineItem => ({
        masterSKU: lineItem.masterSku,
        ean: lineItem.ean,
        price: lineItem.price,
        quantity: lineItem.quantity,
      }))
      return {
        avantLink: {
          purchase: {
            id: data.number,
            currency: data.currency.code,
            netAmount: data.netAmount,
            state: data?.billingAddress?.stateData?.abbr || data?.shippingAddress?.stateData?.abbr,
            country:
              data?.billingAddress?.countryData?.iso || data?.shippingAddress?.countryData?.iso,
            products: productsForAvant,
          },
        },
      }
    },
    rockerBoxPurchase: ({ data }) => {
      return {
        on: {
          order: {
            number: data.number,
            state: data.state,
            email: data.email,
            hashedEmail: getHashedEmail(data.email),
            currency: data.currency.code,
            coupon_code: data?.promotionAdjustment?.code || '',
            shippingAddress: {
              state: data?.shippingAddress?.stateData?.abbr,
              country: data?.shippingAddress?.countryData?.iso,
              city: data?.shippingAddress?.city,
              street: data?.shippingAddress?.address1,
              zip_code: data?.shippingAddress?.zipcode,
            },
            products: data.items.map(lineItem => ({
              sku: lineItem.variantSku,
              size: lineItem.sizeIdentifier,
              price: lineItem.price,
              quantity: lineItem.quantity,
            })),
          },
        },
      }
    },
    // Important: "gtmEcomPurchase" has always to be the last triggered event of all purchase actions!
    gtmEcomPurchase: ({ data }) => {
      const products = data.items.map(item => formatProduct(item))
      return {
        checkoutVersion: data.checkoutVersion,
        deliveryTime: data.estimatedDelivery,
        isLoggedIn: data.isLoggedIn,
        promotionAdjustment: data.promomtionAdjustment,
        ecommerce: {
          currencyCode: data.currency.code,
          purchase: {
            actionField: {
              id: data.number,
              affiliation: data.affiliation,
              revenue: data.total,
              tax: data.taxForGoogleAnalytics,
              shipping: data.shipmentTotal,
            },
            products,
          },
        },
      }
    },
  },

  [ACTIONS.quiz]: {
    gtmEvent: ({ details }) => {
      return {
        eventCategory: 'quiz',
        eventAction: details.action,
        eventLabel: details.label,
        nonInteraction: false,
      }
    },
  },

  [ACTIONS.removeFromCart]: {
    gtmEcomRemoveFromCart: ({ product }) => {
      return {
        ecommerce: {
          remove: {
            products: [
              {
                id: product.productSku,
                price: product.price,
                dimension27: product.size,
                dimension30: product.sku,
                quantity: product.quantity,
              },
            ],
          },
        },
      }
    },
  },

  [ACTIONS.shoeFinder]: {
    gtmEvent: ({ details }) => {
      const label = details.label ? details.label : undefined
      return {
        eventCategory: 'shoe_finder',
        eventAction: details.action,
        eventLabel: label,
        nonInteraction: false,
      }
    },
  },

  [ACTIONS.shoeComparison]: {
    gtmEvent: ({ details }) => {
      return {
        eventCategory: 'shoe_comparison',
        eventAction: details.action,
        eventLabel: details.label,
        nonInteraction: false,
      }
    },
  },

  [ACTIONS.shoeMatcher]: {
    gtmEvent: ({ details }) => {
      return {
        eventCategory: 'shoe_matcher',
        eventAction: details.action,
        eventLabel: details.label,
        nonInteraction: Boolean(details.nonInteraction),
      }
    },
  },

  [ACTIONS.sizeGuide]: {
    gtmEvent: ({ details }) => {
      return {
        eventCategory: 'size_guide',
        eventAction: details.action,
        eventLabel: details.label,
        nonInteraction: false,
      }
    },
  },

  [ACTIONS.subscriptions]: {
    gtmEvent: ({ details }) => {
      return {
        eventCategory: 'subscriptions',
        eventAction: details.action,
        ...(details.label !== '' && { eventLabel: details.label }),
        nonInteraction: Boolean(details.nonInteraction),
      }
    },
  },

  [ACTIONS.userAccount]: {
    gtmEvent: ({ details }) => {
      const label = details.label ? details.label : undefined
      return {
        eventCategory: 'user_account',
        eventAction: details.action,
        eventLabel: label,
        nonInteraction: false,
      }
    },
  },

  [ACTIONS.virtPath]: {
    gtmVirtPath: ({ details }) => {
      return {
        virtPagePath: details.virtPagePath,
        virtPageTitle: details.virtPageTitle,
        ...(details.searchResults ? { searchResults: details.searchResults } : {}), // only for search results page, pass as string
      }
    },
  },
}

On.ACTIONS = ACTIONS
